import { useEffect, useState } from "react";
import { Sparkles, AlarmClock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { fmt } from "./WalletShared";

/* Consolidated rewards dashboard — every tile is clickable and opens the underlying tab. */
export function WalletDashboard({ onNavigate }) {
  const [d, setD] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => { api.get("/wallet/dashboard").then((r) => setD(r.data)).catch(() => setD(null)); }, []);

  const analyze = async () => {
    setAiBusy(true);
    try { const r = await api.post("/wallet/analyze"); setAi(r.data); }
    catch { toast.error("AI analysis unavailable right now — please try again"); }
    setAiBusy(false);
  };

  if (!d) return <p className="text-sm text-muted-foreground">Loading your wallet…</p>;

  const tiles = [
    ["total_miles", "Total airline miles", fmt(d.total_miles), "programs"],
    ["total_points", "Total points", fmt(d.total_points), "programs"],
    ["pooled_miles", "Pooled miles", fmt(d.pooled_miles), "pooling"],
    ["pending_miles", "Pending", fmt(d.pending_miles), "programs"],
    ["accounts", "Loyalty programs", d.accounts, "programs"],
    ["elite_statuses", "Elite statuses", d.elite_statuses, "programs"],
    ["certificates", "Certificates", d.certificates, "certificates"],
    ["companion_certificates", "Companion certs", d.companion_certificates, "certificates"],
    ["upgrade_certificates", "Upgrade certs", d.upgrade_certificates, "certificates"],
    ["credits_count", "Travel credits", d.credits_count, "credits"],
    ["lounge_passes", "Lounge passes", fmt(d.lounge_passes), "benefits"],
    ["benefits", "Benefits tracked", d.benefits, "benefits"],
    ["cards", "Credit cards", d.cards, "cards"],
    ["codes", "Discount codes", d.codes, "codes"],
  ];

  return (
    <div data-testid="wallet-dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {tiles.map(([key, label, val, tab]) => (
          <button key={key} onClick={() => onNavigate(tab)} data-testid={`wallet-tile-${key}`}
            className="border border-zinc-300 bg-white p-3 text-left hover:border-primary transition-colors">
            <div className="font-heading font-900 text-xl tabular-nums">{val}</div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground leading-tight">{label}</div>
          </button>
        ))}
        <button onClick={() => onNavigate("expiring")} data-testid="wallet-tile-expiring"
          className={`border p-3 text-left transition-colors ${d.expiring_soon > 0 ? "border-rose-300 bg-rose-50 hover:border-rose-500" : "border-zinc-300 bg-white hover:border-primary"}`}>
          <div className={`font-heading font-900 text-xl tabular-nums ${d.expiring_soon > 0 ? "text-rose-700" : ""}`}>{d.expiring_soon}</div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground leading-tight flex items-center gap-1"><AlarmClock className="w-3 h-3" /> Expiring soon</div>
        </button>
      </div>
      {Object.keys(d.credits || {}).length > 0 && (
        <div className="mt-2 text-sm" data-testid="wallet-credits-totals">
          <span className="text-muted-foreground">Travel credit balances: </span>
          {Object.entries(d.credits).map(([ccy, v]) => <b key={ccy} className="mr-3">{ccy} {fmt(v)}</b>)}
          <span className="text-xs text-muted-foreground">(currencies are never combined)</span>
        </div>
      )}

      <div className="mt-5 border border-indigo-200 bg-indigo-50/40 p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-700 text-sm text-indigo-900 flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> Analyze My Benefits</div>
            <div className="text-xs text-indigo-900/70">AI scans your stored wallet & upcoming trips for usable benefits, expiring items and gaps. Recommendations only — never invented eligibility.</div>
          </div>
          <button onClick={analyze} disabled={aiBusy} data-testid="wallet-ai-analyze"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-sm font-600 hover:bg-indigo-700 disabled:opacity-50">
            {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {aiBusy ? "Analyzing…" : "Analyze"}
          </button>
        </div>
        {ai && (
          <div className="mt-3 space-y-2" data-testid="wallet-ai-results">
            {ai.insights.map((ins, i) => (
              <div key={i} className="bg-white border border-indigo-100 p-2.5 text-sm" data-testid={`wallet-ai-insight-${i}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-700 uppercase tracking-wider px-1.5 py-0.5 rounded ${ins.confidence === "confirmed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                    {ins.confidence === "confirmed" ? "From your wallet" : "Recommendation"}
                  </span>
                  <b className="text-sm">{ins.title}</b>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{ins.detail}</div>
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground">{ai.disclaimer}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* Expiring Soon tab — everything with a deadline across the whole wallet. */
export function ExpiringSoon({ onNavigate }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/wallet/expiring-soon?days=180").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  if (!items) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (items.length === 0) return <p className="text-sm text-muted-foreground" data-testid="wallet-expiring-empty">Nothing expires in the next 180 days. You're all set.</p>;
  const KIND_TAB = { account: "programs", benefit: "benefits", card: "cards", certificate: "certificates", credit: "credits", code: "codes", pool: "pooling" };
  return (
    <div className="space-y-2" data-testid="wallet-expiring-list">
      <p className="text-xs text-muted-foreground">We remind you automatically at 180 / 90 / 60 / 30 / 14 / 7 / 1 days (in-app + email).</p>
      {items.map((it) => (
        <button key={`${it.kind}-${it.id}`} onClick={() => onNavigate(KIND_TAB[it.kind] || "dashboard")}
          data-testid={`wallet-expiring-${it.id}`}
          className={`w-full text-left flex items-center gap-3 border bg-white px-3 py-2.5 hover:border-primary ${it.days_to_expiry <= 30 ? "border-rose-300" : it.days_to_expiry <= 90 ? "border-amber-300" : "border-zinc-300"}`}>
          <AlarmClock className={`w-4 h-4 shrink-0 ${it.days_to_expiry <= 30 ? "text-rose-600" : "text-amber-600"}`} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-600 truncate">{it.title}</div>
            <div className="text-xs text-muted-foreground">{it.airline || ""}</div>
          </div>
          <div className="text-right shrink-0">
            <div className={`text-sm font-700 tabular-nums ${it.days_to_expiry <= 30 ? "text-rose-700" : "text-amber-700"}`}>
              {it.days_to_expiry < 0 ? "expired" : it.days_to_expiry === 0 ? "today" : `${it.days_to_expiry}d`}
            </div>
            <div className="text-[10px] font-mono text-muted-foreground">{it.expiration_date}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

/* Activity / audit history — append-only. */
export function WalletActivity() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/wallet/activity").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  if (!items) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (items.length === 0) return <p className="text-sm text-muted-foreground">No wallet activity yet.</p>;
  const LABELS = { created: "Added", updated: "Edited", deleted: "Removed", archived: "Archived", active: "Unarchived", applied_to_trip: "Applied to trip", removed_from_trip: "Removed from trip", marked_used: "Marked used", unmarked_used: "Un-marked used", partial_use: "Partial use", member_added: "Pool member added", member_updated: "Pool member updated", member_removed: "Pool member removed", ledger: "Pool transaction", ai_analysis: "AI analysis" };
  return (
    <div className="border border-zinc-300 bg-white divide-y divide-zinc-100 max-h-[28rem] overflow-y-auto" data-testid="wallet-activity-list">
      {items.map((a) => (
        <div key={a.id} className="px-3 py-2 flex items-center gap-3 text-sm" data-testid={`wallet-activity-${a.id}`}>
          <span className="font-mono text-[10px] text-muted-foreground shrink-0 w-28">{(a.at || "").slice(0, 16).replace("T", " ")}</span>
          <span className="text-[10px] font-700 uppercase tracking-wider bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded shrink-0">{a.kind}</span>
          <span className="font-600 shrink-0">{LABELS[a.action] || a.action}</span>
          <span className="text-muted-foreground truncate">{a.detail}</span>
        </div>
      ))}
    </div>
  );
}
